import React from 'react';
import {
  Box,
  TextField,
  Tabs,
  Tab,
  Typography
} from '@mui/material';
import { sortAdminLanguages } from '../utils/languageOrder';

interface MultilingualInputProps {
  label: string;
  value: { [key: string]: string };
  onChange: (value: { [key: string]: string }) => void;
  availableLanguages: string[];
  required?: boolean;
  multiline?: boolean;
  rows?: number;
  /** Lecture seule (ex. question liée avec réponses déjà soumises). */
  disabled?: boolean;
}

const MultilingualInput: React.FC<MultilingualInputProps> = ({
  label,
  value,
  onChange,
  availableLanguages,
  required = false,
  multiline = false,
  rows = 1,
  disabled = false
}) => {
  const sortedLanguages = React.useMemo(
    () => sortAdminLanguages(availableLanguages),
    [availableLanguages]
  );
  const [selectedLang, setSelectedLang] = React.useState(sortedLanguages[0] || 'en');
  const hasUserSelectedLangRef = React.useRef(false);

  React.useEffect(() => {
    if (!sortedLanguages.includes(selectedLang)) {
      setSelectedLang(sortedLanguages[0] || 'en');
    }
  }, [sortedLanguages, selectedLang]);

  // If languages arrive asynchronously, keep default tab in sync
  // until the user explicitly selects a language tab.
  React.useEffect(() => {
    if (hasUserSelectedLangRef.current) return;
    const nextDefault = sortedLanguages[0] || 'en';
    if (nextDefault && selectedLang !== nextDefault) {
      setSelectedLang(nextDefault);
    }
  }, [sortedLanguages, selectedLang]);

  // S'assurer que value est un objet valide avec des valeurs par défaut
  const safeValue = React.useMemo(() => {
    const result = { ...value };
    availableLanguages.forEach(lang => {
      if (result[lang] === undefined) {
        result[lang] = '';
      }
    });
    return result;
  }, [value, availableLanguages]);

  const handleChange = (lang: string, text: string) => {
    onChange({
      ...safeValue,
      [lang]: text
    });
  };

  const hasLabel = typeof label === 'string' && label.trim().length > 0;

  return (
    <Box sx={{ mb: 2 }}>
      {hasLabel && (
        <Typography
          variant="subtitle2"
          color="text.primary"
          sx={{ mt: 2, mb: 0.5, fontWeight: 700, fontSize: '0.95rem' }}
        >
          {label} {required && <span style={{ color: 'red' }}>*</span>}
        </Typography>
      )}
      
      <Tabs
        value={selectedLang}
        onChange={(_, newValue) => {
          hasUserSelectedLangRef.current = true;
          setSelectedLang(newValue);
        }}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 1, minHeight: '36px' }}
      >
        {sortedLanguages.map(lang => (
          <Tab
            key={lang}
            label={lang.toUpperCase()}
            value={lang}
            sx={{
              minHeight: '36px',
              py: 0.5,
              fontSize: '0.875rem',
              bgcolor: safeValue[lang] ? 'primary.50' : 'transparent'
            }}
          />
        ))}
      </Tabs>

      <TextField
        fullWidth
        placeholder={
          hasLabel ? `${label} in ${selectedLang.toUpperCase()}` : undefined
        }
        value={safeValue[selectedLang] || ''}
        onChange={(e) => handleChange(selectedLang, e.target.value)}
        multiline={multiline}
        rows={rows}
        size="small"
        required={required}
        disabled={disabled}
      />
    </Box>
  );
};

export default MultilingualInput;


